import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'; // Keep for potential use
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

import { configurePurchaseOrdersQueryDefaults } from './api/extensions/purchase_orders-business-logic-extension.js';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary.js';
import { PreviewBanner } from './components/PreviewBanner.js';
import { Toaster } from './components/ui/toaster.js';
import { ToastProvider } from './contexts/ToastContext.js';
import { AISidebarProvider } from './contexts/AISidebarContext.js';
import { setAppQueryClient } from './field/offline-queue/appQueryClient.js';
import { startOfflineQueueRunner } from './field/offline-queue/index.js';
import { attachFieldQueryPersistence } from './field/offline-queue/persistFieldQueries.js';
import './field/offline-queue/registerHandlers.js';
import { registerFieldServiceWorker } from './field/registerSW.js';
import { createQueryClient } from './lib/queryClient.js';
import { initSentry } from './lib/sentry.js';
import { routes } from './routes.js';
import { createPersistor } from './store/index.js';
import { store } from './store/singleton.js';
import { initializeAuth } from './store/slices/auth.js';
import { selectTheme, selectColorScheme } from './store/slices/theme.js';


import '/public/styles.css';
import './styles/table-styles.css';
import './styles/saveable-highlight.css';

// Initialize Sentry as early as possible (before any app code runs). No-op when
// VITE_SENTRY_DSN is unset. React render errors are reported from the
// GlobalErrorBoundary, not the global handler.
initSentry();

// Prevent scroll-to-change on all number inputs globally.
// When a user scrolls over a focused number input, the browser changes the value.
// This captures wheel events on number inputs and blurs them instead.
document.addEventListener('wheel', (e) => {
  const el = document.activeElement;
  if (el instanceof HTMLInputElement && el.type === 'number') {
    el.blur();
  }
}, { passive: true });

// Get the root element
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error("Could not find root element with id 'root'");
}

// Create Query Client (no hydration needed for CSR)
const queryClient = createQueryClient();

// Install per-entity query defaults. PO detail queries need 404-tolerant
// retry behavior so post-mutation refetches of stale/deleted PO ids fail
// fast (no retry storm, no misleading console error). See the extension
// file for the full rationale.
configurePurchaseOrdersQueryDefaults(queryClient);

// Expose the app's QueryClient to background offline-queue replay handlers so
// a queued activity.setStatus that replays on reconnect can invalidate the
// same cache the UI renders against (card rKkWHT_XhbXU, part C symmetry).
// NB: the cross-device stale-read fix (part B) is now baked into every
// generated list/detail hook (staleTime:0 + refetchOnMount:'always') via the
// hooks generator template, so no per-entity query-default wiring is needed.
setAppQueryClient(queryClient);

// Initialize persistence and auth synchronously first
console.log('Creating persistor...');
// Create the persistor instance
const persistor = createPersistor(store);
// store.dispatch(initializeAuth()); // Defer this until after initial render
console.log('Persistor initialization complete. Auth initialization deferred.');

// Apply theme directly without React components
const applyTheme = () => {
  const state = store.getState();
  const theme = selectTheme(state);
  const scheme = selectColorScheme(state);
  
  const root = document.documentElement;
  if (theme === 'dark') {
    if (!root.classList.contains('dark')) {
      root.classList.add('dark');
    }
  } else if (root.classList.contains('dark')) {
    root.classList.remove('dark');
  }
  ['theme-blue','theme-green','theme-purple','theme-rose','theme-plain'].forEach(c => root.classList.remove(c));
  root.classList.add(`theme-${scheme}`);
  console.log('[Theme] Applied:', theme, 'scheme:', scheme);
};

// Apply theme immediately
applyTheme();

// Subscribe to store changes for theme updates
store.subscribe(applyTheme);

// Start the offline mutation queue runner. Cheap (one setInterval + one
// 'online' listener) and shared by warehouse + jobsite slices. Handlers are
// registered via the side-effect import above (registerHandlers.ts).
startOfflineQueueRunner();

// Persist field-relevant React Query reads to localStorage so workers see
// the last-known data on reload + offline. Filters by query-key prefix so
// the desktop app's large list queries aren't persisted. NO-OP outside the
// field PWA (`/field` scope) so the desktop office app never restores stale
// field offline snapshots — e.g. activities (card rKkWHT_XhbXU).
attachFieldQueryPersistence(queryClient);

// NOW create the router
const router = createBrowserRouter(routes);

// Define the main App component structure (uses the router instance)
const App = () => (
  <GlobalErrorBoundary>
    <Provider store={store}>
      {/* Wrap the application with PersistGate */}
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          {/* Wrap with branded toast provider */}
          <ToastProvider>
            {/* Wrap with AI sidebar global state (persists across navigation) */}
            <AISidebarProvider>
              {/* Ensure fallbackElement is present */}
              <RouterProvider
                router={router}
              />
              {/* Add ReactQueryDevtools back if needed during development */}
              {/* <ReactQueryDevtools initialIsOpen={false} /> */}
              {/* Add the custom Toaster for toast notifications */}
              <Toaster />
              {/* "Preview environment" tag on preview builds only (office + field) */}
              <PreviewBanner />
              {/* AI chatbot disabled for this version — keep code for future use */}
              {/* <GlobalAIChatSidebar /> */}
              {/* <GlobalAIAssistantButton /> */}
            </AISidebarProvider>
          </ToastProvider>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  </GlobalErrorBoundary>
);

// Use standard client-side rendering
console.log('Using client-side rendering (CSR)');
const root = ReactDOM.createRoot(rootElement);
// In development mode, we'll dispatch initializeAuth() to fetch user data
// In production, window.__USER__ is injected by the server

root.render(<App />);
console.log('Client-side rendering initiated');

// --- Setup Auth Listener AFTER Initial Render ---
// Always dispatch initializeAuth() after initial render to verify session via API
console.log('Dispatching initializeAuth() to verify session with backend...');
// Wrap in try-catch to prevent auth issues from breaking the app
try {
  store.dispatch(initializeAuth()).catch((error) => {
    console.log('Auth initialization skipped:', error?.message || 'Auth service not available');
  });
} catch (error) {
  console.log('Auth initialization skipped:', error);
}
// Note: window.__USER__ can still provide initial data, but initializeAuth ensures verification.
 
// Auth is now server-side (HttpOnly cookies).
// No onAuthStateChange listener needed — initializeAuth() calls GET /auth/me on page load.

// Register the field PWA service worker once, on boot. This is a no-op in dev
// (the SW isn't built there) and guards on `'serviceWorker' in navigator`, so
// it's safe for the entry bootstrap. See client/field/registerSW.ts.
registerFieldServiceWorker();
